/**
 * End Simulation Confirmation Modal
 * Shows a confirmation dialog before ending the simulation
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

interface EndSimulationModalProps {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
}

export function EndSimulationModal({
    isOpen,
    onConfirm,
    onCancel,
    isLoading = false
}: EndSimulationModalProps) {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                        onClick={onCancel}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="fixed inset-0 flex items-center justify-center z-50 p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-slate-200 flex items-start gap-4">
                                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-lg font-bold text-slate-900">
                                        End Simulation?
                                    </h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Are you sure you want to end this simulation?
                                    </p>
                                </div>
                                <button
                                    onClick={onCancel}
                                    className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
                                    disabled={isLoading}
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="px-6 py-4">
                                <p className="text-sm text-slate-600">
                                    Ending the simulation will generate your final evaluation report. 
                                    You won't be able to continue this simulation afterwards.
                                </p>
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-3">
                                <button
                                    onClick={onCancel}
                                    disabled={isLoading}
                                    className="flex-1 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Continue Simulation
                                </button>
                                <button
                                    onClick={onConfirm}
                                    disabled={isLoading}
                                    className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <motion.div
                                                className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                            />
                                            Generating...
                                        </>
                                    ) : (
                                        'End & View Report'
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export default EndSimulationModal;




